import React, { useEffect, useState, useCallback } from 'react';
import { COLORS } from '../../utils/constants';
import { tradingAPI } from '../../services/api';
import { useTradingToast } from '../../hooks/useTradingToast';
import { useLanguage } from '../../context/LanguageContext';
import ConfirmModal from './ConfirmModal';

import { X, TrendingUp, TrendingDown, AlertCircle, RefreshCw } from 'lucide-react';

const PositionsList = ({ symbol, refresh, embedded = false, onSetSLTP }) => {
  const { t } = useLanguage();
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [closingTrade, setClosingTrade] = useState(null);
  const [marketPrices, setMarketPrices] = useState({});
  const [lastPriceUpdate, setLastPriceUpdate] = useState(null);
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, tradeId: null, symbol: '' });
  const { toast } = useTradingToast();

  // Load market prices for all positions
  const loadMarketPrices = useCallback(async (tradesList) => {
    const uniqueSymbols = [...new Set(tradesList.map(t => t.symbol))];
    const prices = {};
    
    await Promise.all(uniqueSymbols.map(async (sym) => {
      try {
        const response = await tradingAPI.getPrice(sym);
        prices[sym] = response.data.price;
      } catch (error) {
        console.error(`Failed to load price for ${sym}:`, error);
      }
    }));
    
    setMarketPrices(prices);
    setLastPriceUpdate(new Date());
  }, []);

  useEffect(() => {
    loadTrades();
  }, [symbol, refresh]);

  // Auto-refresh prices every 5 seconds
  useEffect(() => {
    if (trades.length > 0) {
      loadMarketPrices(trades);
      const interval = setInterval(() => loadMarketPrices(trades), 5000);
      return () => clearInterval(interval);
    }
  }, [trades, loadMarketPrices]);

  const loadTrades = async () => {
    try {
      const response = await tradingAPI.getTrades('open');
      const filteredTrades = symbol 
        ? response.data.filter(t => t.symbol === symbol)
        : response.data;
      setTrades(filteredTrades);
      if (filteredTrades.length > 0) {
        loadMarketPrices(filteredTrades);
      }
    } catch (error) {
      console.error('Failed to load trades:', error);
    } finally {
      setLoading(false);
    }
  };

  // Calculate real-time PnL based on market price
  const calculatePnL = (trade) => {
    const markPrice = marketPrices[trade.symbol] || trade.entry_price;
    const entryPrice = trade.entry_price;
    const size = (trade.margin * trade.leverage) / entryPrice;
    
    let pnl;
    if (trade.side === 'long') {
      pnl = (markPrice - entryPrice) * size;
    } else {
      pnl = (entryPrice - markPrice) * size;
    }
    
    const roi = (pnl / trade.margin) * 100;
    
    return { pnl, roi, markPrice };
  };

  const handleCloseTrade = async (tradeId) => {
    const trade = trades.find(t => t.id === tradeId);
    if (trade) {
      setConfirmModal({
        isOpen: true,
        tradeId,
        symbol: trade.symbol,
        side: trade.side,
      });
    }
  };

  const executeCloseTrade = async () => {
    const { tradeId, symbol: tradeSymbol } = confirmModal;
    setConfirmModal({ isOpen: false, tradeId: null, symbol: '' });
    setClosingTrade(tradeId);
    
    try {
      const result = await tradingAPI.closeTrade(tradeId);
      const pnl = result?.data?.pnl || 0;
      toast.positionClosed(tradeSymbol, pnl);
      await loadTrades();
    } catch (error) {
      console.error('Failed to close trade:', error);
      toast.apiError(error, 'Ошибка закрытия позиции');
    } finally {
      setClosingTrade(null);
    }
  };

  const formatPnL = (pnl) => {
    const sign = pnl >= 0 ? '+' : '';
    return `${sign}$${Math.abs(pnl).toFixed(2)}`;
  };

  const formatROI = (roi) => {
    const sign = roi >= 0 ? '+' : '';
    return `${sign}${roi.toFixed(2)}%`;
  };

  if (loading) {
    return (
      <div style={{
        height: embedded ? '100%' : 'auto',
        minHeight: '150px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: COLORS.textSecondary,
        backgroundColor: COLORS.background,
      }}>
        Loading positions...
      </div>
    );
  }

  // Embedded mode - Binance/Bybit professional style
  if (embedded) {
    return (
      <div style={{ 
        backgroundColor: '#161a1e',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      }}>
        {trades.length === 0 ? (
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column',
            alignItems: 'center', 
            justifyContent: 'center', 
            flex: 1,
            color: '#848e9c',
            gap: '8px',
          }}>
            <AlertCircle size={24} style={{ opacity: 0.5 }} />
            <div style={{ fontSize: '13px' }}>Нет открытых позиций</div>
          </div>
        ) : (
          <>
            {/* Table Header - Binance/Bybit style */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1.4fr 1fr 1fr 1fr 1fr 1.3fr 1.2fr',
              padding: '10px 16px',
              backgroundColor: '#1e2329',
              borderBottom: '1px solid #2b3139',
              fontSize: '11px',
              color: '#5e6673',
              fontWeight: '500',
              textTransform: 'uppercase',
              letterSpacing: '0.3px',
              flexShrink: 0,
            }}>
              <span>Символ</span>
              <span style={{ textAlign: 'right' }}>Размер</span>
              <span style={{ textAlign: 'right' }}>Вход</span>
              <span style={{ textAlign: 'right' }}>Марк</span>
              <span style={{ textAlign: 'right' }}>Ликв.</span>
              <span style={{ textAlign: 'right' }}>PNL (ROE)</span>
              <span style={{ textAlign: 'right' }}>Действия</span>
            </div>

            {/* Positions rows - Binance/Bybit style */}
            <div style={{ flex: 1, overflowY: 'auto' }}>
              {trades.map((trade, index) => {
                const isLong = trade.side === 'long';
                const { pnl, roi, markPrice } = calculatePnL(trade);
                const isProfitable = pnl >= 0;
                const size = (trade.margin * trade.leverage) / trade.entry_price;
                
                // Calculate liquidation price
                const liqPrice = isLong
                  ? trade.entry_price * (1 - 1 / trade.leverage * 0.8)
                  : trade.entry_price * (1 + 1 / trade.leverage * 0.8);
                
                return (
                  <div
                    key={trade.id}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1.4fr 1fr 1fr 1fr 1fr 1.3fr 1.2fr',
                      padding: '12px 16px',
                      backgroundColor: index % 2 === 0 ? '#161a1e' : '#1a1e23',
                      borderBottom: '1px solid #252930',
                      alignItems: 'center',
                      transition: 'background-color 0.15s ease',
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#1e2329'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = index % 2 === 0 ? '#161a1e' : '#1a1e23'}
                  >
                    {/* Symbol & Side */}
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                        <span style={{ 
                          color: '#eaecef',
                          fontWeight: '600',
                          fontSize: '14px',
                          letterSpacing: '0.2px',
                        }}>
                          {trade.symbol.replace('USDT', '')}
                        </span>
                        <span style={{
                          padding: '2px 6px',
                          borderRadius: '2px',
                          fontSize: '10px',
                          fontWeight: '600',
                          backgroundColor: isLong ? '#0ecb81' : '#f6465d',
                          color: '#fff',
                          textTransform: 'uppercase',
                        }}>
                          {isLong ? 'Long' : 'Short'}
                        </span>
                      </div>
                      <div style={{ 
                        fontSize: '11px', 
                        color: '#848e9c',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                      }}>
                        <span style={{ color: '#f0b90b', fontWeight: '600' }}>{trade.leverage}x</span>
                        <span>{trade.margin_mode === 'cross' ? 'Cross' : 'Isolated'}</span>
                      </div>
                    </div>

                    {/* Size */}
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ color: '#eaecef', fontFamily: 'monospace', fontSize: '12px', fontWeight: '500', marginBottom: '2px' }}>
                        {size.toFixed(4)}
                      </div>
                      <div style={{ color: '#5e6673', fontSize: '10px' }}>
                        ${trade.margin.toFixed(0)}
                      </div>
                    </div>

                    {/* Entry Price */}
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ color: '#eaecef', fontFamily: 'monospace', fontSize: '12px' }}>
                        {trade.entry_price.toFixed(2)}
                      </div>
                    </div>

                    {/* Mark Price */}
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ 
                        color: isProfitable ? '#0ecb81' : '#f6465d', 
                        fontFamily: 'monospace',
                        fontSize: '12px',
                        fontWeight: '600',
                      }}>
                        {markPrice.toFixed(2)}
                      </div>
                    </div>

                    {/* Liquidation Price */}
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ color: '#f6465d', fontFamily: 'monospace', fontSize: '12px' }}>
                        {liqPrice.toFixed(2)}
                      </div>
                    </div>

                    {/* PNL */}
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ 
                        color: isProfitable ? '#0ecb81' : '#f6465d',
                        fontFamily: 'monospace',
                        fontSize: '13px',
                        fontWeight: '700',
                        marginBottom: '2px',
                      }}>
                        {isProfitable ? '+' : ''}{pnl.toFixed(2)}
                      </div>
                      <div style={{ 
                        color: isProfitable ? '#0ecb81' : '#f6465d',
                        fontSize: '11px',
                        fontWeight: '500',
                      }}>
                        ({isProfitable ? '+' : ''}{roi.toFixed(2)}%)
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
                      <button
                        onClick={() => onSetSLTP && onSetSLTP(trade)}
                        style={{
                          padding: '5px 10px',
                          background: 'linear-gradient(180deg, #f5bc00 0%, #d4a300 100%)',
                          border: 'none',
                          color: '#0b0e11',
                          borderRadius: '4px',
                          fontSize: '11px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          boxShadow: '0 2px 4px rgba(245, 188, 0, 0.2)',
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.filter = 'brightness(1.1)'}
                        onMouseLeave={(e) => e.currentTarget.style.filter = 'brightness(1)'}
                      >
                        SL/TP
                      </button>
                      <button
                        onClick={() => handleCloseTrade(trade.id)}
                        disabled={closingTrade === trade.id}
                        style={{
                          padding: '5px 10px',
                          background: 'transparent',
                          border: '1px solid #f6465d',
                          color: '#f6465d',
                          borderRadius: '4px',
                          fontSize: '11px',
                          fontWeight: '600',
                          cursor: closingTrade === trade.id ? 'not-allowed' : 'pointer',
                          opacity: closingTrade === trade.id ? 0.5 : 1,
                          transition: 'all 0.2s ease',
                        }}
                        onMouseEnter={(e) => {
                          if (closingTrade !== trade.id) {
                            e.currentTarget.style.background = '#f6465d';
                            e.currentTarget.style.color = '#fff';
                          }
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'transparent';
                          e.currentTarget.style.color = '#f6465d';
                        }}
                      >
                        {closingTrade === trade.id ? t('closing') : t('close')}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* Confirmation Modal */}
        <ConfirmModal
          isOpen={confirmModal.isOpen}
          onClose={() => setConfirmModal({ isOpen: false, tradeId: null, symbol: '' })}
          onConfirm={executeCloseTrade}
          title={t("closePosition")}
          message={`Вы уверены, что хотите закрыть позицию ${confirmModal.symbol}?`}
          confirmText={t("close")}
          cancelText="Отмена"
          type="danger"
          loading={closingTrade === confirmModal.tradeId}
        />
      </div>
    );
  }

  // Desktop full view
  return (
    <div
      style={{
        backgroundColor: COLORS.card,
        border: `1px solid ${COLORS.border}`,
        borderRadius: '12px',
        padding: '20px',
      }}
    >
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '20px',
      }}>
        <h2 style={{ fontSize: '18px', fontWeight: 'bold', color: COLORS.text, margin: 0 }}>
          Open Positions
        </h2>
        {lastPriceUpdate && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '11px', color: COLORS.textSecondary }}>
              Updated: {lastPriceUpdate.toLocaleTimeString()}
            </span>
            <button
              onClick={() => loadMarketPrices(trades)}
              style={{
                background: 'none',
                border: 'none',
                padding: '4px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <RefreshCw size={14} color={COLORS.textSecondary} />
            </button>
          </div>
        )}
      </div>

      {trades.length === 0 ? (
        <div style={{ 
          textAlign: 'center', 
          color: COLORS.textSecondary, 
          padding: '40px' 
        }}>
          No open positions
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                <th style={{ padding: '12px', textAlign: 'left', color: COLORS.textSecondary, fontSize: '12px' }}>Symbol</th>
                <th style={{ padding: '12px', textAlign: 'left', color: COLORS.textSecondary, fontSize: '12px' }}>Side</th>
                <th style={{ padding: '12px', textAlign: 'right', color: COLORS.textSecondary, fontSize: '12px' }}>Entry</th>
                <th style={{ padding: '12px', textAlign: 'right', color: COLORS.textSecondary, fontSize: '12px' }}>Mark</th>
                <th style={{ padding: '12px', textAlign: 'right', color: COLORS.textSecondary, fontSize: '12px' }}>Margin</th>
                <th style={{ padding: '12px', textAlign: 'right', color: COLORS.textSecondary, fontSize: '12px' }}>PnL</th>
                <th style={{ padding: '12px', textAlign: 'right', color: COLORS.textSecondary, fontSize: '12px' }}>ROI</th>
                <th style={{ padding: '12px', textAlign: 'center', color: COLORS.textSecondary, fontSize: '12px' }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {trades.map((trade) => {
                const { pnl, roi, markPrice } = calculatePnL(trade);
                const isProfitable = pnl >= 0;
                
                return (
                  <tr key={trade.id} style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                    <td style={{ padding: '12px', color: COLORS.text, fontWeight: '600' }}>
                      {trade.symbol}
                    </td>
                    <td style={{ padding: '12px' }}>
                      <span style={{
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: '600',
                        backgroundColor: trade.side === 'long' ? `${COLORS.success}20` : `${COLORS.danger}20`,
                        color: trade.side === 'long' ? COLORS.success : COLORS.danger,
                      }}>
                        {trade.side.toUpperCase()} {trade.leverage}x
                      </span>
                    </td>
                    <td style={{ padding: '12px', textAlign: 'right', color: COLORS.text, fontFamily: 'monospace' }}>
                      ${trade.entry_price.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </td>
                    <td style={{ 
                      padding: '12px', 
                      textAlign: 'right', 
                      color: isProfitable ? COLORS.success : COLORS.danger, 
                      fontFamily: 'monospace',
                      fontWeight: '500',
                    }}>
                      ${markPrice.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </td>
                    <td style={{ padding: '12px', textAlign: 'right', color: COLORS.text, fontFamily: 'monospace' }}>
                      ${trade.margin.toFixed(2)}
                    </td>
                    <td style={{ 
                      padding: '12px', 
                      textAlign: 'right', 
                      color: isProfitable ? COLORS.success : COLORS.danger,
                      fontWeight: '600',
                      fontFamily: 'monospace',
                    }}>
                      {formatPnL(pnl)}
                    </td>
                    <td style={{ 
                      padding: '12px', 
                      textAlign: 'right',
                    }}>
                      <span style={{
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: '700',
                        backgroundColor: isProfitable ? `${COLORS.success}20` : `${COLORS.danger}20`,
                        color: isProfitable ? COLORS.success : COLORS.danger,
                      }}>
                        {formatROI(roi)}
                      </span>
                    </td>
                    <td style={{ padding: '12px', textAlign: 'center' }}>
                      <button
                        onClick={() => handleCloseTrade(trade.id)}
                        disabled={closingTrade === trade.id}
                        style={{
                          padding: '6px 16px',
                          backgroundColor: COLORS.danger,
                          color: '#FFF',
                          border: 'none',
                          borderRadius: '6px',
                          fontSize: '12px',
                          cursor: closingTrade === trade.id ? 'not-allowed' : 'pointer',
                          opacity: closingTrade === trade.id ? 0.5 : 1,
                        }}
                      >
                        {closingTrade === trade.id ? t('closing') : t('close')}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Confirmation Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ isOpen: false, tradeId: null, symbol: '' })}
        onConfirm={executeCloseTrade}
        title={t("closePosition")}
        message={`Вы уверены, что хотите закрыть позицию ${confirmModal.symbol}?`}
        confirmText={t("close")}
        cancelText="Отмена"
        type="danger"
        loading={closingTrade === confirmModal.tradeId}
      />

      {/* Animation styles */}
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default PositionsList;
